import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { Snowflake, Drop, Leaf } from '@phosphor-icons/react';

const productImage = '/product-image.png';
const KEY_LOGO_URL = '/elemantra-key-logo.png';

export const ProductSection = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
    },
  };

  const features = [
    { icon: Snowflake, label: t.product.feature1 },
    { icon: Drop, label: t.product.feature2 },
    { icon: Leaf, label: t.product.feature3 },
  ];

  return (
    <section
      id="product"
      ref={ref}
      className="relative py-24 md:py-40 bg-[#FDFBF7] overflow-hidden"
      data-testid="product-section"
    >
      {/* Watermark Key Logo */}
      <div 
        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/3 pointer-events-none"
        style={{ opacity: 0.03 }}
      >
        <img 
          src={KEY_LOGO_URL} 
          alt="" 
          className="w-[500px] h-auto object-contain"
          aria-hidden="true"
        />
      </div>

      {/* Grain Texture */}
      <div 
        className="absolute inset-0 opacity-[0.03] pointer-events-none mix-blend-multiply"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center"
        >
          {/* Text Content */}
          <motion.div
            variants={containerVariants}
            className="lg:col-span-5 order-2 lg:order-1"
          >
            <motion.span
              variants={itemVariants}
              className="text-xs tracking-[0.25em] uppercase text-[#B89C6A] font-outfit font-semibold"
            >
              {t.product.overline}
            </motion.span>
            
            <motion.h2
              variants={itemVariants}
              className="font-cormorant text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#2A2624] mt-4 mb-6"
            >
              {t.product.title}
            </motion.h2>

            <motion.p
              variants={itemVariants}
              className="font-outfit text-lg text-[#5C5650] mb-12"
            >
              {t.product.subtitle}
            </motion.p>

            {/* Features */}
            <motion.div
              variants={containerVariants}
              className="space-y-6"
            >
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="flex items-center gap-4 group"
                >
                  <div className="w-12 h-12 rounded-full bg-[#F3EFE6] flex items-center justify-center group-hover:bg-[#E5DED0] transition-colors duration-500">
                    <feature.icon 
                      size={24} 
                      weight="light"
                      className="text-[#B89C6A]"
                    />
                  </div>
                  <span className="font-outfit text-sm tracking-wide text-[#2A2624] uppercase">
                    {feature.label}
                  </span>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Product Image */}
          <motion.div
            variants={itemVariants}
            className="lg:col-span-7 order-1 lg:order-2"
          >
            <div className="relative">
              {/* Decorative elements */}
              <div className="absolute -top-8 -right-8 w-32 h-32 border border-[#E5DED0] -z-10" />
              <div className="absolute -bottom-8 -left-8 w-48 h-48 bg-[#F3EFE6] -z-10" />
              
              <img
                src={productImage}
                alt="Elemantra Product"
                className="w-full h-[400px] md:h-[550px] object-contain"
                data-testid="product-image"
              />
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
